import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useCriarChamado } from '../../hooks/useChamados';
import styles from './ChamadoModal.module.css';

const ChamadoModal = ({ show, onHide, onSuccess }) => {
  const criarMut = useCriarChamado();

  const [form, setForm] = useState({
    tipo:      'bug',
    titulo:    '',
    descricao: '',
    prioridade:'media',
  });
  const [anexos, setAnexos]     = useState([]); // [{ file, url, isImg }]
  const [alertMsg, setAlertMsg] = useState(null);

  const MAX_ANEXOS = 10;

  const handleChange = e => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const appendArquivos = files => {
    const novos = files.map(file => {
      const isImg = (file.type || '').startsWith('image/');
      return { file, isImg, url: isImg ? URL.createObjectURL(file) : null };
    });
    if (novos.length) setAnexos(prev => [...prev, ...novos].slice(0, MAX_ANEXOS));
  };

  const handleAddFiles = e => {
    appendArquivos(Array.from(e.target.files || []));
    e.target.value = ''; // permite re-selecionar o mesmo arquivo
  };

  // Colar print direto (Ctrl+V) — captura a imagem do clipboard enquanto o modal está aberto
  useEffect(() => {
    if (!show) return;
    const onPaste = e => {
      const itens = Array.from(e.clipboardData?.items || []);
      const imgs = itens.filter(it => it.type && it.type.startsWith('image/'));
      if (!imgs.length) return;
      const files = imgs.map((it, i) => {
        const blob = it.getAsFile();
        return blob ? new File([blob], `print-${Date.now()}-${i}.png`, { type: blob.type || 'image/png' }) : null;
      }).filter(Boolean);
      appendArquivos(files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const removeAnexo = idx => {
    setAnexos(prev => {
      const alvo = prev[idx];
      if (alvo?.url) URL.revokeObjectURL(alvo.url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.titulo.trim()) return;

    try {
      const payload = {
        ...form,
        url_origem: window.location.href,
      };
      const result = await criarMut.mutateAsync(payload);

      // Upload dos anexos escolhidos — o endpoint aceita 1 por requisição,
      // então enviamos em sequência (cada um vira uma linha em chamado_anexos).
      // result é { success, chamado: { id, numero, ... } }
      const chamadoCriado = result?.chamado;
      if (anexos.length && chamadoCriado?.id) {
        for (const a of anexos) {
          const fd = new FormData();
          fd.append('arquivo', a.file);
          await fetch(`/api/chamados/${chamadoCriado.id}/anexos`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: fd,
          });
        }
        // libera os previews
        anexos.forEach(a => a.url && URL.revokeObjectURL(a.url));
        setAnexos([]);
      }

      // Sucesso: fecha o modal imediatamente e deixa o toast (fora do modal,
      // no ChamadoFloatButton) informar que o chamado foi aberto.
      onSuccess?.(chamadoCriado);
    } catch {
      setAlertMsg({ type: 'danger', text: 'Erro ao criar chamado. Tente novamente.' });
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" aria-labelledby="modal-chamado-titulo">
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title id="modal-chamado-titulo">Abrir chamado</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {alertMsg && (
            <Alert variant={alertMsg.type} className="py-2 small">
              {alertMsg.text}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label className={styles.labelSmall}>Tipo</Form.Label>
            <Form.Select name="tipo" value={form.tipo} onChange={handleChange} aria-label="Tipo do chamado">
              <option value="bug">Bug / Erro</option>
              <option value="atualizacao">Atualização / Melhoria</option>
              <option value="nova_funcionalidade">Nova funcionalidade</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className={styles.labelSmall}>Título *</Form.Label>
            <Form.Control
              name="titulo"
              value={form.titulo}
              onChange={handleChange}
              placeholder="Descreva o problema em uma linha..."
              required
              maxLength={200}
              aria-label="Título do chamado"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className={styles.labelSmall}>Descrição detalhada</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="descricao"
              value={form.descricao}
              onChange={handleChange}
              placeholder="Passos para reproduzir, o que esperava acontecer, o que aconteceu..."
              aria-label="Descrição do chamado"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className={styles.labelSmall}>Prioridade</Form.Label>
            <Form.Select name="prioridade" value={form.prioridade} onChange={handleChange} aria-label="Prioridade">
              <option value="baixa">Baixa</option>
              <option value="media">Média (padrão)</option>
              <option value="alta">Alta</option>
              <option value="critica">Crítica</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label className={styles.labelSmall}>
              Anexos (opcional) — várias imagens, PDF, etc.{' '}
              <span style={{ fontWeight: 400, color: '#64748b' }}>ou cole um print com Ctrl+V 📋</span>
            </Form.Label>
            <Form.Control
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              onChange={handleAddFiles}
              aria-label="Arquivos de anexo"
              disabled={anexos.length >= MAX_ANEXOS}
            />
            {anexos.length > 0 && (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                  {anexos.map((a, i) => (
                    <div
                      key={i}
                      title={a.file.name}
                      style={{ position: 'relative', width: 72, height: 72, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {a.isImg
                        ? <img src={a.url} alt={a.file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ fontSize: 10, padding: 4, textAlign: 'center', color: '#64748b', wordBreak: 'break-word', lineHeight: 1.2 }}>📎 {a.file.name}</span>}
                      <button
                        type="button"
                        onClick={() => removeAnexo(i)}
                        title="Remover"
                        style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(15,23,42,0.72)', color: '#fff', fontSize: 12, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >×</button>
                    </div>
                  ))}
                </div>
                <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                  {anexos.length} de {MAX_ANEXOS} anexo(s){anexos.length >= MAX_ANEXOS ? ' — limite atingido' : ''}
                </small>
              </>
            )}
          </Form.Group>

          <small className="text-muted" style={{ fontSize:'0.75rem' }}>
            Página atual: {window.location.pathname}
          </small>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} type="button">
            Cancelar
          </Button>
          <Button
            type="submit"
            className={styles.btnEnviar}
            disabled={!form.titulo.trim() || criarMut.isPending}
          >
            {criarMut.isPending ? (
              <><Spinner animation="border" size="sm" className="me-1" /> Enviando...</>
            ) : 'Abrir chamado'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ChamadoModal;
